/**
 * SCR-019 — Class detail container (Teacher)
 * Route: /teacher/classes/:classId
 * Container with header + 4-tab nav. Each tab rendered via nested route Outlet.
 * Default tab: /roster (redirect if no child route matched).
 * Mobile-first: vertical tab rail stacks above content <768px; left rail ≥768px.
 */
import {
  createFileRoute,
  Link,
  Outlet,
  redirect,
  useNavigate,
  useLocation,
} from '@tanstack/react-router';
import { ArrowLeft, AlertCircle, RefreshCw } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useTeacherClassDetail } from '@client/lib/queries/use-teacher';
import { ApiError } from '@client/lib/api-client';

export const Route = createFileRoute('/teacher/classes/$classId')({
  beforeLoad: ({ location, params }) => {
    // Redirect bare /teacher/classes/:classId (no child route) to the
    // default Danh sách tab. Don't redirect when a child path is present
    // — that includes the tabbed routes (roster/attendance/grades/
    // assignments) AND the flat live-session route
    // (classes.$classId.sessions.$sessionId).
    const segments = location.pathname.split('/').filter(Boolean);
    // segments for the bare route are ['teacher', 'classes', '{cid}'] (length 3).
    if (segments.length === 3) {
      throw redirect({
        to: '/teacher/classes/$classId/roster',
        params: { classId: params.classId },
      });
    }
  },
  component: ClassDetailPage,
});

// ---------------------------------------------------------------------------
const STATUS_META: Record<string, { label: string; variant: 'warning' | 'info' | 'success' | 'secondary' | 'destructive' }> = {
  planned:   { label: 'Chờ khai giảng', variant: 'warning' },
  open:      { label: 'Mở ghi danh',    variant: 'info' },
  running:   { label: 'Đang học',        variant: 'success' },
  finished:  { label: 'Đã kết thúc',    variant: 'secondary' },
  cancelled: { label: 'Đã hủy',         variant: 'destructive' },
};

const TABS = [
  { path: 'roster',      label: 'Danh sách' },
  { path: 'attendance',  label: 'Điểm danh' },
  { path: 'grades',      label: 'Điểm' },
  { path: 'assignments', label: 'Bài tập' },
];

// ---------------------------------------------------------------------------
function ClassDetailPage() {
  const { classId } = Route.useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const { data: cls, isLoading, isError, error, refetch } = useTeacherClassDetail(classId);

  // 403 → redirect to /403
  if (isError && error instanceof ApiError && error.status === 403) {
    void navigate({ to: '/403', replace: true });
    return null;
  }

  // 404 → redirect to /403 (no /404 route; access issue shown as forbidden)
  if (isError && error instanceof ApiError && error.status === 404) {
    void navigate({ to: '/403', replace: true });
    return null;
  }

  const activeTab = TABS.find((t) =>
    location.pathname.endsWith(`/${t.path}`),
  )?.path ?? 'roster';

  const isCancelledOrFinished = cls?.status === 'cancelled' || cls?.status === 'finished';

  return (
    <div className="space-y-4">
      {/* Back link */}
      <Link
        to="/teacher"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        Lớp của tôi
      </Link>

      {/* Class header */}
      {isLoading && (
        <div className="space-y-2">
          <Skeleton className="h-7 w-64" />
          <Skeleton className="h-4 w-96" />
        </div>
      )}

      {isError && !isLoading && !(error instanceof ApiError && [403, 404].includes(error.status)) && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="flex items-center justify-between gap-4">
            <span>Không tải được thông tin lớp.</span>
            <Button variant="outline" size="sm" onClick={() => void refetch()}>
              <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
              Thử lại
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {cls && (
        <>
          {/* Banner: cancelled / finished */}
          {isCancelledOrFinished && (
            <Alert variant="destructive" className="py-2">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                {cls.status === 'cancelled'
                  ? 'Lớp đã bị hủy — chỉ được xem, không thể chỉnh sửa.'
                  : 'Lớp đã kết thúc — chỉ được xem, không thể chỉnh sửa.'}
              </AlertDescription>
            </Alert>
          )}

          {/* Header block */}
          <div className="rounded-xl border border-border bg-card p-4 space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-lg font-bold text-foreground leading-tight">{cls.name}</h1>
              <Badge variant={STATUS_META[cls.status]?.variant} className="text-xs">
                {STATUS_META[cls.status]?.label ?? cls.status}
              </Badge>
            </div>
            <p className="text-xs text-muted-foreground flex flex-wrap gap-x-3 gap-y-1">
              <span className="font-mono">{cls.code}</span>
              {cls.courseName && <span>{cls.courseName}</span>}
              {cls.branchName && <span>{cls.branchName}</span>}
              <span>
                Vai trò: {cls.teacherRole === 'lead' ? 'Giáo viên chính' : 'Hỗ trợ'}
              </span>
              <span>
                Sĩ số: {cls.currentEnrollment}/{cls.capacity}
              </span>
            </p>
          </div>

          {/* Vertical tab rail + content (mobile: rail stacks above content) */}
          <div className="flex flex-col gap-4 md:flex-row md:gap-6">
            {/* Tab rail — vertical; full-width above content <768px, left rail ≥768px */}
            <nav
              aria-label="Mục lớp học"
              className="flex w-full shrink-0 flex-col gap-1 md:w-48 md:border-r md:border-border md:pr-2"
            >
              {TABS.map((tab) => {
                const isActive = activeTab === tab.path;
                const tabTo = `/teacher/classes/${classId}/${tab.path}` as
                  '/teacher/classes/$classId/roster'
                  | '/teacher/classes/$classId/attendance'
                  | '/teacher/classes/$classId/grades'
                  | '/teacher/classes/$classId/assignments';
                return (
                  <Link
                    key={tab.path}
                    to={tabTo}
                    params={{ classId }}
                    aria-current={isActive ? 'page' : undefined}
                    className={`relative flex min-h-[44px] items-center rounded-md px-3 py-2 text-sm font-medium transition-colors
                      ${isActive
                        ? 'bg-muted text-foreground'
                        : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground'
                      }`}
                  >
                    {isActive && (
                      <span
                        aria-hidden
                        className="absolute inset-y-1.5 left-0 w-0.5 rounded-full bg-foreground"
                      />
                    )}
                    {tab.label}
                  </Link>
                );
              })}
            </nav>

            {/* Tab content outlet */}
            <div className="min-w-0 flex-1">
              <Outlet />
            </div>
          </div>
        </>
      )}

      {/* Outlet when class not loaded (error/loading states render above) */}
      {!cls && <Outlet />}
    </div>
  );
}
